/**
 * User Data Storage Layer
 *
 * Provides a unified interface for storing and retrieving user data using localStorage.
 * Handles serialization, validation, and error recovery.
 */

import {
  UserFavorites,
  UserHistory,
  UserConfigurations,
  UserNotes,
  UserSettings,
  UserActivityLog,
  UserDataExport,
  UserActivity,
  DisplayPreferences,
  NotificationPreferences,
  PrivacyPreferences,
  DefaultFilters,
} from './user-data-types';

// ============================================================================
// Storage Keys
// ============================================================================

export const STORAGE_KEYS = {
  FAVORITES: 'tech-stack-favorites',
  HISTORY: 'tech-stack-history',
  CONFIGURATIONS: 'tech-stack-configurations',
  NOTES: 'tech-stack-notes',
  SETTINGS: 'tech-stack-settings',
  ACTIVITIES: 'tech-stack-activities',
  VERSION: 'tech-stack-data-version',
} as const;

export const CURRENT_VERSION = '1.0.0';

// ============================================================================
// Storage Provider Class
// ============================================================================

class LocalStorageProvider {
  private isAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  save<T>(key: string, data: T): void {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return;
    }

    try {
      const serialized = JSON.stringify(data);
      localStorage.setItem(key, serialized);
    } catch (e) {
      console.error(`Failed to save data for key: ${key}`, e);
    }
  }

  load<T>(key: string, defaultValue?: T): T | null {
    if (!this.isAvailable()) {
      return defaultValue || null;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue || null;
      }
      return JSON.parse(item) as T;
    } catch (e) {
      console.error(`Failed to load data for key: ${key}`, e);
      return defaultValue || null;
    }
  }

  remove(key: string): void {
    if (!this.isAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove data for key: ${key}`, e);
    }
  }

  clear(): void {
    if (!this.isAvailable()) {
      return;
    }

    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.error('Failed to clear storage', e);
    }
  }
}

export const storage = new LocalStorageProvider();

// ============================================================================
// Favorites Storage
// ============================================================================

export function saveFavorites(favorites: UserFavorites): void {
  storage.save(STORAGE_KEYS.FAVORITES, favorites);
}

export function loadFavorites(): UserFavorites {
  const defaultValue: UserFavorites = {
    technologies: [],
    lastUpdated: new Date().toISOString(),
  };
  return storage.load(STORAGE_KEYS.FAVORITES, defaultValue) || defaultValue;
}

// ============================================================================
// History Storage
// ============================================================================

export function saveHistory(history: UserHistory): void {
  storage.save(STORAGE_KEYS.HISTORY, history);
}

export function loadHistory(): UserHistory {
  return storage.load(STORAGE_KEYS.HISTORY, {
    comparisons: [],
    recommendations: [],
    searches: [],
    lastUpdated: new Date().toISOString(),
  }) || {
    comparisons: [],
    recommendations: [],
    searches: [],
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// Configurations Storage
// ============================================================================

export function saveConfigurations(configurations: UserConfigurations): void {
  storage.save(STORAGE_KEYS.CONFIGURATIONS, configurations);
}

export function loadConfigurations(): UserConfigurations {
  return storage.load(STORAGE_KEYS.CONFIGURATIONS, {
    saved: [],
    templates: [],
    lastUpdated: new Date().toISOString(),
  }) || {
    saved: [],
    templates: [],
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// Notes Storage
// ============================================================================

export function saveNotes(notes: UserNotes): void {
  storage.save(STORAGE_KEYS.NOTES, notes);
}

export function loadNotes(): UserNotes {
  return storage.load(STORAGE_KEYS.NOTES, {
    technologyNotes: [],
    decisionNotes: [],
    lastUpdated: new Date().toISOString(),
  }) || {
    technologyNotes: [],
    decisionNotes: [],
    lastUpdated: new Date().toISOString(),
  };
}

// ============================================================================
// Settings Storage
// ============================================================================

const DEFAULT_SETTINGS: UserSettings = {
  displayPreferences: {
    theme: 'auto',
    compactMode: false,
    defaultView: 'grid',
    showScores: true,
    showConcerns: true,
  },
  notifications: {
    newTechnologies: false,
    trendingChanges: false,
    weeklyDigest: false,
  },
  privacy: {
    saveHistory: true,
    analytics: true,
  },
  defaultFilters: {},
  lastUpdated: new Date().toISOString(),
};

export function saveSettings(settings: UserSettings): void {
  storage.save(STORAGE_KEYS.SETTINGS, settings);
}

export function loadSettings(): UserSettings {
  return storage.load(STORAGE_KEYS.SETTINGS, DEFAULT_SETTINGS) || DEFAULT_SETTINGS;
}

// ============================================================================
// Activity Log Storage
// ============================================================================

const MAX_ACTIVITIES = 100; // Keep only the last 100 activities

export function saveActivities(activities: UserActivityLog): void {
  // Trim to max size
  const trimmed = {
    ...activities,
    activities: activities.activities.slice(-MAX_ACTIVITIES),
  };
  storage.save(STORAGE_KEYS.ACTIVITIES, trimmed);
}

export function loadActivities(): UserActivityLog {
  return storage.load(STORAGE_KEYS.ACTIVITIES, {
    activities: [],
    lastUpdated: new Date().toISOString(),
  }) || {
    activities: [],
    lastUpdated: new Date().toISOString(),
  };
}

export function addActivity(activity: Omit<UserActivity, 'id' | 'timestamp'>): void {
  const activities = loadActivities();
  const newActivity: UserActivity = {
    ...activity,
    id: generateId(),
    timestamp: new Date().toISOString(),
  };

  activities.activities.push(newActivity);
  activities.lastUpdated = new Date().toISOString();
  saveActivities(activities);
}

// ============================================================================
// Export/Import Functionality
// ============================================================================

export function exportUserData(): UserDataExport {
  return {
    exportedAt: new Date().toISOString(),
    version: CURRENT_VERSION,
    favorites: loadFavorites(),
    history: loadHistory(),
    configurations: loadConfigurations(),
    notes: loadNotes(),
    settings: loadSettings(),
    activities: loadActivities(),
  };
}

export function exportUserDataAsJSON(): string {
  const data = exportUserData();
  return JSON.stringify(data, null, 2);
}

export function exportUserDataAsFile(): void {
  const data = exportUserDataAsJSON();
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tech-stack-data-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function importUserData(data: UserDataExport, merge: boolean = false): void {
  // Validate version
  if (data.version !== CURRENT_VERSION) {
    console.warn(`Data version mismatch. Expected ${CURRENT_VERSION}, got ${data.version}`);
  }

  if (merge) {
    // Merge with existing data
    const existingFavorites = loadFavorites();
    const existingHistory = loadHistory();
    const existingConfigurations = loadConfigurations();
    const existingNotes = loadNotes();
    const existingActivities = loadActivities();

    // Merge favorites (avoid duplicates)
    const mergedFavorites: UserFavorites = {
      technologies: [
        ...existingFavorites.technologies,
        ...data.favorites.technologies.filter(
          (f) => !existingFavorites.technologies.some((e) => e.technologyId === f.technologyId)
        ),
      ],
      lastUpdated: new Date().toISOString(),
    };

    // Merge history (avoid duplicates by ID)
    const mergedHistory: UserHistory = {
      comparisons: mergeByIdArray(existingHistory.comparisons, data.history.comparisons),
      recommendations: mergeByIdArray(existingHistory.recommendations, data.history.recommendations),
      searches: mergeByIdArray(existingHistory.searches, data.history.searches),
      lastUpdated: new Date().toISOString(),
    };

    // Merge configurations
    const mergedConfigurations: UserConfigurations = {
      saved: mergeByIdArray(existingConfigurations.saved, data.configurations.saved),
      templates: mergeByIdArray(existingConfigurations.templates, data.configurations.templates),
      lastUpdated: new Date().toISOString(),
    };

    // Merge notes
    const mergedNotes: UserNotes = {
      technologyNotes: mergeByIdArray(existingNotes.technologyNotes, data.notes.technologyNotes),
      decisionNotes: mergeByIdArray(existingNotes.decisionNotes, data.notes.decisionNotes),
      lastUpdated: new Date().toISOString(),
    };

    // Merge activities
    const mergedActivities: UserActivityLog = {
      activities: mergeByIdArray(existingActivities.activities, data.activities.activities).slice(
        -MAX_ACTIVITIES
      ),
      lastUpdated: new Date().toISOString(),
    };

    saveFavorites(mergedFavorites);
    saveHistory(mergedHistory);
    saveConfigurations(mergedConfigurations);
    saveNotes(mergedNotes);
    saveActivities(mergedActivities);
  } else {
    // Replace existing data
    saveFavorites(data.favorites);
    saveHistory(data.history);
    saveConfigurations(data.configurations);
    saveNotes(data.notes);
    saveActivities(data.activities);
  }

  // Always import settings (overwrite)
  saveSettings(data.settings);
}

export function importUserDataFromFile(file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string) as UserDataExport;
        importUserData(data, false);
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function mergeByIdArray<T extends { id: string }>(existing: T[], incoming: T[]): T[] {
  const map = new Map<string, T>();

  // Add existing items
  existing.forEach((item) => map.set(item.id, item));

  // Add or update with incoming items
  incoming.forEach((item) => map.set(item.id, item));

  return Array.from(map.values());
}

// ============================================================================
// Data Migration
// ============================================================================

export function migrateDataIfNeeded(): void {
  const currentVersion = storage.load<string>(STORAGE_KEYS.VERSION, '0.0.0');

  if (currentVersion === CURRENT_VERSION) {
    return;
  }

  // Perform migrations here if needed
  console.log(`Migrating data from version ${currentVersion} to ${CURRENT_VERSION}`);

  // Update version
  storage.save(STORAGE_KEYS.VERSION, CURRENT_VERSION);
}

// Initialize migration on import
if (typeof window !== 'undefined') {
  migrateDataIfNeeded();
}
